import React, { useCallback, useMemo } from "react";
import Stack from "@mui/material/Stack";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import { SelectChangeEvent } from "@mui/material";
import Typography from "@mui/material/Typography";

export interface InstallAgeValue {
  min?: number | null;
  max?: number | null;
}

interface InstallAgeFilterProps {
  value: InstallAgeValue;
  onChange: (value: InstallAgeValue) => void;
  disabled?: boolean;
}

// Generate year options (0-10 years)
const YEAR_OPTIONS = Array.from({ length: 11 }, (_, i) => ({
  value: i,
  label: i === 0 ? "No limit" : `${i} Year${i === 1 ? "" : "s"}`,
}));

// Generate month options (0-11 months)
const MONTH_OPTIONS = Array.from({ length: 12 }, (_, i) => ({
  value: i,
  label: i === 0 ? "No limit" : `${i} Month${i === 1 ? "" : "s"}`,
}));

// Convert years/months to total months
const convertToMonths = (years: number, months: number): number => {
  if (years === 0 && months === 0) return 0;
  return years * 12 + months;
};

// Convert total months to years/months
const convertFromMonths = (
  totalMonths: number
): { years: number; months: number } => {
  if (totalMonths === 0) return { years: 0, months: 0 };
  const years = Math.floor(totalMonths / 12);
  const months = totalMonths % 12;
  return { years, months };
};

export const InstallAgeFilter: React.FC<InstallAgeFilterProps> = React.memo(
  ({ value, onChange, disabled = false }) => {
    // Convert current values to years/months
    const minYearsMonths = useMemo(() => {
      if (value.min === undefined || value.min === null)
        return { years: 0, months: 0 };
      return convertFromMonths(value.min);
    }, [value.min]);

    const maxYearsMonths = useMemo(() => {
      if (value.max === undefined || value.max === null)
        return { years: 0, months: 0 };
      return convertFromMonths(value.max);
    }, [value.max]);

    const handleMinYearChange = useCallback(
      (event: SelectChangeEvent<number>) => {
        const years = Number(event.target.value);
        const { months } = minYearsMonths;
        const totalMonths =
          years === 0 && months === 0
            ? undefined
            : convertToMonths(years, months);

        let newMax = value.max;
        // If min becomes greater than max, adjust max to match min
        if (
          totalMonths !== undefined &&
          value.max !== undefined &&
          value.max !== null &&
          totalMonths > value.max
        ) {
          newMax = totalMonths;
        }

        onChange({ min: totalMonths, max: newMax });
      },
      [minYearsMonths.months, value.max, onChange]
    );

    const handleMinMonthChange = useCallback(
      (event: SelectChangeEvent<number>) => {
        const months = Number(event.target.value);
        const { years } = minYearsMonths;
        const totalMonths =
          years === 0 && months === 0
            ? undefined
            : convertToMonths(years, months);

        let newMax = value.max;
        // If min becomes greater than max, adjust max to match min
        if (
          totalMonths !== undefined &&
          value.max !== undefined &&
          value.max !== null &&
          totalMonths > value.max
        ) {
          newMax = totalMonths;
        }

        onChange({ min: totalMonths, max: newMax });
      },
      [minYearsMonths.years, value.max, onChange]
    );

    const handleMaxYearChange = useCallback(
      (event: SelectChangeEvent<number>) => {
        const years = Number(event.target.value);
        const { months } = maxYearsMonths;
        const totalMonths =
          years === 0 && months === 0
            ? undefined
            : convertToMonths(years, months);

        let newMin = value.min;
        // If max becomes less than min, adjust min to match max
        if (
          totalMonths !== undefined &&
          value.min !== undefined &&
          value.min !== null &&
          totalMonths < value.min
        ) {
          newMin = totalMonths;
        }

        onChange({ min: newMin, max: totalMonths });
      },
      [maxYearsMonths.months, value.min, onChange]
    );

    const handleMaxMonthChange = useCallback(
      (event: SelectChangeEvent<number>) => {
        const months = Number(event.target.value);
        const { years } = maxYearsMonths;
        const totalMonths =
          years === 0 && months === 0
            ? undefined
            : convertToMonths(years, months);

        let newMin = value.min;
        // If max becomes less than min, adjust min to match max
        if (
          totalMonths !== undefined &&
          value.min !== undefined &&
          value.min !== null &&
          totalMonths < value.min
        ) {
          newMin = totalMonths;
        }

        onChange({ min: newMin, max: totalMonths });
      },
      [maxYearsMonths.years, value.min, onChange]
    );

    return (
      <Stack spacing={2}>
        <Stack direction="row" spacing={2} alignItems="center">
          <Typography variant="body2" sx={{ fontWeight: 500, minWidth: 60 }}>
            Minimum
          </Typography>
          <FormControl size="small" sx={{ minWidth: 120 }} disabled={disabled}>
            <InputLabel>Years</InputLabel>
            <Select
              value={minYearsMonths.years}
              onChange={handleMinYearChange}
              label="Years"
            >
              {YEAR_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 120 }} disabled={disabled}>
            <InputLabel>Months</InputLabel>
            <Select
              value={minYearsMonths.months}
              onChange={handleMinMonthChange}
              label="Months"
            >
              {MONTH_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>

        <Stack direction="row" spacing={2} alignItems="center">
          <Typography variant="body2" sx={{ fontWeight: 500, minWidth: 60 }}>
            Maximum
          </Typography>
          <FormControl size="small" sx={{ minWidth: 120 }} disabled={disabled}>
            <InputLabel>Years</InputLabel>
            <Select
              value={maxYearsMonths.years}
              onChange={handleMaxYearChange}
              label="Years"
            >
              {YEAR_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 120 }} disabled={disabled}>
            <InputLabel>Months</InputLabel>
            <Select
              value={maxYearsMonths.months}
              onChange={handleMaxMonthChange}
              label="Months"
            >
              {MONTH_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
      </Stack>
    );
  }
);

InstallAgeFilter.displayName = "InstallAgeFilter";
